// src/lib/bffAuth.ts
//
// Confidential-BFF browser session client (M2-BFF, matrix A16).
// The browser NEVER holds Keycloak tokens: login/logout are full-page
// redirects through the API, the session id lives in an HttpOnly +
// SameSite=Lax cookie, and mutating API calls must echo the per-session
// CSRF token (from GET /api/auth/session) in the X-CSRF-Token header.
//
// Default ON. Set VITE_BFF_AUTH=0 only for local bearer-token debugging
// (keycloak-js PKCE against the public client, memory-only tokens).

export interface BffSession {
  username: string;
  sub?: string;
  issuer?: string;
  groups: string[];
  accountType: string;
  csrfToken: string;
}

export const BFF_ENABLED =
  ((import.meta as unknown as { env: Record<string, string> }).env
    ?.VITE_BFF_AUTH ?? "1") !== "0";

let cached: BffSession | null = null;
let sessionEpoch = 0;

/** Load the current BFF session (null when signed out). Caches the
 * result so authHeaders() can attach the CSRF token synchronously. */
export async function fetchBffSession(): Promise<BffSession | null> {
  const epoch = sessionEpoch;
  try {
    const r = await fetch("/api/auth/session", {
      credentials: "same-origin",
    });
    if (epoch !== sessionEpoch) return null;
    if (!r.ok) {
      cached = null;
      return null;
    }
    const profile = (await r.json()) as BffSession;
    if (epoch !== sessionEpoch) return null;
    cached = profile;
    return cached;
  } catch {
    if (epoch === sessionEpoch) cached = null;
    return null;
  }
}

/** Send the browser to the BFF login endpoint (full-page redirect to
 * Keycloak and back through the server-side code exchange). */
export function bffLogin(next?: string): void {
  const target =
    next ?? window.location.pathname + window.location.search;
  window.location.assign(`/api/auth/login?next=${encodeURIComponent(target)}`);
}

/** Destroy the server-side session, then follow the Keycloak logout
 * URL so the SSO session is closed too. */
export async function bffLogout(): Promise<void> {
  const csrf = cached?.csrfToken;
  // Fence responses already awaiting fetch or JSON parsing at logout.
  sessionEpoch += 1;
  cached = null;
  try {
    const r = await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "same-origin",
      headers: csrf ? { "X-CSRF-Token": csrf } : {},
    });
    if (!r.ok) throw new Error("BFF logout was not acknowledged");
    const body = (await r.json()) as { logoutUrl?: unknown } | null;
    if (typeof body?.logoutUrl !== "string" || !body.logoutUrl) {
      throw new Error("Invalid BFF logout response");
    }
    window.location.assign(body.logoutUrl);
  } catch {
    // Controlled reload, not a claim of local/IdP logout success. The
    // provider intentionally does not await this promise; never reject it.
    window.location.assign("/");
  }
}

/** CSRF header for mutating API calls in BFF mode. Empty object when
 * signed out — the API fails closed (401/403). */
export function bffCsrfHeaders(): Record<string, string> {
  return cached ? { "X-CSRF-Token": cached.csrfToken } : {};
}
